import { useQuery } from '@tanstack/react-query';
import { LiveComment, MatchDetailsOdds } from '@/interfaces/liveGamesType';
import { getLiveOddsByMatch } from '@/pages/Dashboard/V2/services/api';

interface UseLiveMatchCommentsOptions {
  refetchInterval?: number | false;
  enabled?: boolean;
  meta?: {
    errorMessage?: string;
  };
}

interface ProcessedCommentData {
  formattedTime: number[];
  liveOddsData: any | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Converte tempo de jogo em segundos
 */
export const convertTimeToSeconds = (time: string): number => {
  if (!time) return 0;
  try {
    const [minutes, seconds] = time.split(':').map(Number);
    return (isNaN(minutes) ? 0 : minutes) * 60 + (isNaN(seconds) ? 0 : seconds);
  } catch (e) {
    console.warn('Erro ao converter tempo:', time, e);
    return 0;
  }
};

/**
 * Converte segundos em tempo de jogo formatado
 */
export const convertSecondsToTime = (seconds: number): string => {
  const mins = Math.floor(Math.abs(seconds) / 60);
  const secs = Math.abs(seconds) % 60;
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

/**
 * Processa comentários ao vivo para gerar dados de momentum
 */
const processLiveComments = (comments: LiveComment[] = [], homeTeamName: string = '', awayTeamName: string = ''): number[] => {
  console.log('processLiveComments chamado com:', {
    commentsLength: comments?.length || 0,
    commentsType: typeof comments,
    isArray: Array.isArray(comments),
    homeTeamName,
    awayTeamName
  });

  // Verificar se comments é um array válido
  if (!comments || !Array.isArray(comments)) {
    console.warn('Comentários inválidos recebidos:', comments);
    return [];
  }

  // Verificar se o array está vazio
  if (comments.length === 0) {
    console.log('Array de comentários vazio');
    return [];
  }

  try {
    console.log(`Processando ${comments.length} comentários`);

    // Verificar a estrutura do primeiro comentário para depuração
    if (comments.length > 0) {
      console.log('Estrutura do primeiro comentário:', JSON.stringify(comments[0], null, 2));

      // Verificar se os comentários têm a estrutura esperada
      const hasValidStructure = comments.every(comment =>
        comment && typeof comment === 'object' && 'time' in comment && 'text' in comment
      );

      if (!hasValidStructure) {
        console.warn('Alguns comentários não têm a estrutura esperada (time e text)');
        // Filtrar apenas comentários válidos
        comments = comments.filter(comment =>
          comment && typeof comment === 'object' && 'time' in comment && 'text' in comment
        );
        console.log(`Após filtragem, restaram ${comments.length} comentários válidos`);
      }
    }

    // Se após a filtragem não sobrar nenhum comentário válido, retornar array vazio
    if (comments.length === 0) {
      return [];
    }

    // Ordenar comentários por tempo
    const sortedComments = [...comments].sort((a, b) => {
      const timeA = convertTimeToSeconds(a.time);
      const timeB = convertTimeToSeconds(b.time);
      return timeA - timeB;
    });

    console.log(`Comentários ordenados por tempo`);

    // Mapear cada comentário para um valor de momentum
    const result = sortedComments.map((comment, index) => {
      // Verificar se o comentário tem texto
      if (!comment.text) {
        console.warn(`Comentário ${index} não tem texto`);
        return 0;
      }

      // Texto do comentário em minúsculo para facilitar a comparação
      const commentText = comment.text.toLowerCase();

      // Nomes das equipes em minúsculo
      const homeTeamLower = homeTeamName ? homeTeamName.toLowerCase() : '';
      const awayTeamLower = awayTeamName ? awayTeamName.toLowerCase() : '';

      // Verificar se o comentário menciona posse de bola, ataque ou ação de uma equipe
      const isHomeTeamAction = commentText.includes(homeTeamLower) ||
        (commentText.includes('home') && !commentText.includes('away')) ||
        (homeTeamLower && (
          commentText.includes('possession') && commentText.indexOf(homeTeamLower) < 20 ||
          commentText.includes('attack') && commentText.indexOf(homeTeamLower) < 20 ||
          commentText.includes('dangerous') && commentText.indexOf(homeTeamLower) < 20 ||
          commentText.includes('shot') && commentText.indexOf(homeTeamLower) < 20 ||
          commentText.includes('corner') && commentText.indexOf(homeTeamLower) < 20
        ));

      const isAwayTeamAction = commentText.includes(awayTeamLower) ||
        (commentText.includes('away') && !commentText.includes('home')) ||
        (awayTeamLower && (
          commentText.includes('possession') && commentText.indexOf(awayTeamLower) < 20 ||
          commentText.includes('attack') && commentText.indexOf(awayTeamLower) < 20 ||
          commentText.includes('dangerous') && commentText.indexOf(awayTeamLower) < 20 ||
          commentText.includes('shot') && commentText.indexOf(awayTeamLower) < 20 ||
          commentText.includes('corner') && commentText.indexOf(awayTeamLower) < 20
        ));

      // Ignorar comentários de meio tempo ou fim de jogo
      if (
        commentText.includes("half time") ||
        commentText.includes("full time")
      ) {
        return 0;
      }

      // Calcular a intensidade da ação com base no tipo de comentário
      let intensity = 10; // Valor base

      if (commentText.includes('dangerous')) intensity = 20;
      if (commentText.includes('shot')) intensity = 25;
      if (commentText.includes('goal')) intensity = 30;
      if (commentText.includes('possession')) intensity = 5;

      // Retornar valor positivo para time da casa, negativo para visitante
      if (isHomeTeamAction) {
        return intensity;
      } else if (isAwayTeamAction) {
        return -intensity;
      }

      return 0; // Neutro para outros comentários
    });

    console.log(`Gerados ${result.length} valores de momentum`);
    console.log('Primeiros 5 valores:', result.slice(0, 5));

    // Se não houver valores significativos, gerar alguns valores padrão para teste
    if (result.every(val => val === 0)) {
      console.log('Nenhum valor significativo encontrado, gerando valores de teste');
      // Gerar alguns valores aleatórios para teste
      return Array.from({ length: 10 }, () => Math.floor(Math.random() * 30) * (Math.random() > 0.5 ? 1 : -1));
    }

    return result;
  } catch (error) {
    console.error('Erro ao processar comentários:', error);
    return [];
  }
};

/**
 * Hook para buscar e processar comentários ao vivo de uma partida
 */
export const useLiveMatchComments = (
  matchId: string | undefined,
  isAuthenticated: boolean,
  options: UseLiveMatchCommentsOptions = {}
): ProcessedCommentData => {
  const {
    refetchInterval = 60000, // Atualizar a cada 1 minuto por padrão
    enabled = true,
    meta = {}
  } = options;

  // Verificar se temos um ID de partida válido
  const isValidMatchId = !!matchId && matchId !== 'undefined' && matchId !== 'null';
  const effectiveEnabled = enabled && isValidMatchId;

  // Buscar dados da API
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['liveOdds', matchId],
    queryFn: async () => {
      console.log(`Executando queryFn para matchId: ${matchId}`);

      try {
        // Verificar se temos um ID de partida válido
        if (!isValidMatchId) {
          console.warn('ID da partida não fornecido ou inválido');
          return {
            match_id: '',
            live_comments: [],
            match_hometeam_name: '',
            match_awayteam_name: '',
            live_odds: []
          };
        }

        // Buscar dados da API
        console.log(`Chamando getLiveOddsByMatch para matchId: ${matchId}`);

        try {
          const result = await getLiveOddsByMatch(matchId!, isAuthenticated);
          console.log('Dados recebidos da API:', result ? Object.keys(result) : 'null');

          // Verificar se temos dados válidos
          if (!result) {
            console.warn('Nenhum dado recebido da API');
            return {
              match_id: matchId,
              live_comments: [],
              match_hometeam_name: '',
              match_awayteam_name: '',
              live_odds: []
            };
          }

          // Verificar se temos dados para a partida específica
          if (!result[matchId!]) {
            console.warn(`Dados para a partida ${matchId} não encontrados na resposta`);
            console.log('Chaves disponíveis:', Object.keys(result));

            // Verificar se há alguma chave que possa conter os dados
            const availableKeys = Object.keys(result);
            if (availableKeys.length > 0) {
              console.log(`Tentando usar a primeira chave disponível: ${availableKeys[0]}`);
              const firstMatchData = result[availableKeys[0]];

              if (firstMatchData && typeof firstMatchData === 'object') {
                return {
                  match_id: matchId,
                  live_comments: Array.isArray(firstMatchData.live_comments) ? firstMatchData.live_comments : [],
                  match_hometeam_name: firstMatchData.match_hometeam_name || '',
                  match_awayteam_name: firstMatchData.match_awayteam_name || '',
                  live_odds: Array.isArray(firstMatchData.live_odds) ? firstMatchData.live_odds : []
                };
              }
            }

            return {
              match_id: matchId,
              live_comments: [],
              match_hometeam_name: '',
              match_awayteam_name: '',
              live_odds: []
            };
          }

          // Extrair os dados da partida específica
          const matchData = result[matchId!];
          console.log(`Dados da partida ${matchId}:`, matchData ? Object.keys(matchData) : 'null');

          // Verificar se temos comentários ao vivo
          if (!matchData.live_comments) {
            console.warn('Campo live_comments não encontrado');
            matchData.live_comments = [];
          } else if (!Array.isArray(matchData.live_comments)) {
            console.warn('live_comments não é um array:', typeof matchData.live_comments);

            // Tentar converter para array se for string JSON
            if (typeof matchData.live_comments === 'string') {
              try {
                const parsedComments = JSON.parse(matchData.live_comments);
                if (Array.isArray(parsedComments)) {
                  console.log('Convertido live_comments de string JSON para array');
                  matchData.live_comments = parsedComments;
                } else {
                  console.warn('live_comments é uma string JSON, mas não um array');
                  matchData.live_comments = [];
                }
              } catch (e) {
                console.warn('Erro ao analisar live_comments como JSON:', e);
                matchData.live_comments = [];
              }
            } else {
              matchData.live_comments = [];
            }
          } else {
            console.log(`Encontrados ${matchData.live_comments.length} comentários ao vivo`);
          }

          return {
            match_id: matchId,
            live_comments: matchData.live_comments || [],
            match_hometeam_name: matchData.match_hometeam_name || '',
            match_awayteam_name: matchData.match_awayteam_name || '',
            live_odds: matchData.live_odds || []
          };
        } catch (apiError) {
          console.error('Erro ao chamar a API:', apiError);
          // Se ocorrer um erro 500, podemos tentar uma abordagem alternativa ou simplesmente retornar dados vazios
          console.log('Retornando dados vazios devido a erro na API');
          return {
            match_id: matchId,
            live_comments: [],
            match_hometeam_name: '',
            match_awayteam_name: '',
            live_odds: []
          };
        }
      } catch (error) {
        console.error(meta.errorMessage || 'Erro ao buscar dados de comentários ao vivo:', error);
        // Retornar um objeto vazio em vez de lançar erro
        return {
          match_id: matchId || '',
          live_comments: [],
          match_hometeam_name: '',
          match_awayteam_name: '',
          live_odds: []
        };
      }
    },
    enabled: effectiveEnabled,
    refetchInterval: effectiveEnabled ? refetchInterval : false,
    staleTime: 30000, // Considerar dados atualizados por 30 segundos
    retry: 1, // Tentar novamente apenas 1 vez em caso de erro
    retryDelay: 5000, // Esperar 5 segundos entre as tentativas
  });

  // Processar comentários para criar dados de momentum
  const formattedTime = processLiveComments(
    data?.live_comments,
    data?.match_hometeam_name,
    data?.match_awayteam_name
  );

  return {
    formattedTime,
    liveOddsData: data || null,
    isLoading,
    isError,
    error
  };
};

export default useLiveMatchComments;